import pkg from '../../package.json';

// Payment Methods
export const PAYMENT_METHODS = {
  QRIS: 'FLIPEWALLET',
  EPAYMENT: 'EPAYMENT',
} as const;

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  QRIS: 'QRIS',
  EPAYMENT: 'rwallet',
};

export const BANK_CODES = {
  QRIS: 'qris',
  EPAYMENT: 'epayment',
} as const;

// Transaction Status
export const TRANSACTION_STATUS = {
  COMPLETED: 'COMPLETED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;

export const TRANSACTION_STATUS_LABELS: Record<string, string> = {
  COMPLETED: 'Completed',
  VOIDED: 'Voided',
  REFUNDED: 'Refunded',
};

export const TRANSACTION_STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-success-100 text-success-700',
  VOIDED: 'bg-gray-100 text-gray-700',
  REFUNDED: 'bg-warning-100 text-warning-700',
};

// User Roles
export const USER_ROLES = {
  CASHIER: 'CASHIER',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  GENERATE_CODE: '/api/auth/generate-code',
  LOGIN: '/api/auth/login',
  REFRESH: '/api/auth/refresh',
  LOGOUT: '/api/auth/logout',
  ME: '/api/auth/me',

  // Shops
  USER_SHOPS: (userId: number) => `/api/users/${userId}/shops`,
  SHOP_DETAILS: (userId: number, shopId: number) => `/api/users/${userId}/shops/${shopId}`,
  SHOP_CONFIG: (userId: number, shopId: number) => `/api/users/${userId}/shops/${shopId}/config`,

  // Customers
  CUSTOMER_SEARCH: (shopId: number) => `/api/shops/${shopId}/customers/search`,

  // Online (e-commerce) orders
  ONLINE_ORDERS: (shopId: number) => `/api/shops/${shopId}/ecommerce-transactions`,
  ONLINE_ORDERS_SUMMARY: (shopId: number) => `/api/shops/${shopId}/ecommerce-transactions/summary`,
  ONLINE_ORDER: (shopId: number, txId: string) => `/api/shops/${shopId}/ecommerce-transactions/${txId}`,
  ONLINE_ORDER_STATUS: (shopId: number, txId: string) => `/api/shops/${shopId}/ecommerce-transactions/${txId}/status`,
  ONLINE_ORDER_COMPLETE: (shopId: number, txId: string) => `/api/shops/${shopId}/ecommerce-transactions/${txId}/complete`,

  // Top-up
  TOPUP: (shopId: number) => `/api/shops/${shopId}/topup`,
  TOPUP_PAYMENT_METHODS: (shopId: number) => `/api/shops/${shopId}/topup/payment-methods`,

  // Notifications & push
  NOTIFICATIONS: (shopId: number) => `/api/shops/${shopId}/notifications`,
  NOTIFICATION_READ: (shopId: number) => `/api/shops/${shopId}/notifications/read`,
  NOTIFICATIONS_READ_ALL: (shopId: number) => `/api/shops/${shopId}/notifications/read-all`,
  FCM_TOKEN: (shopId: number) => `/api/shops/${shopId}/notifications/fcm-token`,
  FCM_TOKEN_DELETE: (shopId: number) => `/api/shops/${shopId}/notifications/delete/fcm-token`,

  // POS session (required for push delivery)
  SESSION_OPEN: (shopId: number) => `/api/shops/${shopId}/session/open`,
  SESSION_CLOSE: (shopId: number) => `/api/shops/${shopId}/session/close`,

  // Attendance
  ATTENDANCE_STAFF: (shopId: number) => `/api/shops/${shopId}/attendance/staff`,
  ATTENDANCE_CHECK_IN: (shopId: number) => `/api/shops/${shopId}/attendance/check-in`,
  ATTENDANCE_CHECK_OUT: (shopId: number) => `/api/shops/${shopId}/attendance/check-out`,

  // Petty cash
  PETTY_CASH: (shopId: number) => `/api/shops/${shopId}/petty-cash`,
  PETTY_CASH_SUMMARY: (shopId: number) => `/api/shops/${shopId}/petty-cash/summary`,
  // Reimbursement
  REIMBURSEMENT: (shopId: number) => `/api/shops/${shopId}/reimbursement`,
  REIMBURSEMENT_WALLETS: (shopId: number) => `/api/shops/${shopId}/reimbursement/wallets`,
  // Stock request
  STOCK_REQUEST: (shopId: number) => `/api/shops/${shopId}/stock-request`,
  STOCK_REQUEST_PRODUCTS: (shopId: number) => `/api/shops/${shopId}/stock-request/products`,
  // Stock opname
  STOCK_OPNAME: (shopId: number) => `/api/shops/${shopId}/stock-opname`,
  STOCK_OPNAME_DETAIL: (shopId: number, id: number) => `/api/shops/${shopId}/stock-opname/${id}`,

  // Catalog
  CATALOG: (shopId: number) => `/api/shops/${shopId}/catalog`,
  SEARCH_PRODUCTS: (shopId: number) => `/api/shops/${shopId}/catalog/search`,
  BARCODE_LOOKUP: (shopId: number, barcode: string) => `/api/shops/${shopId}/catalog/barcode/${barcode}`,
  PRODUCT_DETAILS: (shopId: number, id: number) => `/api/shops/${shopId}/catalog/product/${id}`,
  CATEGORIES: (shopId: number) => `/api/shops/${shopId}/catalog/categories`,
  PRICE_CHECK: (shopId: number, barcode: string) => `/api/shops/${shopId}/catalog/price-check/${barcode}`,

  // Cart
  CALCULATE_CART: (shopId: number) => `/api/shops/${shopId}/cart/calculate`,
  VALIDATE_STOCK: (shopId: number) => `/api/shops/${shopId}/cart/validate-stock`,

  // Held Carts
  HELD_CARTS: (shopId: number) => `/api/shops/${shopId}/held-carts`,
  HELD_CART_BY_ID: (shopId: number, id: number) => `/api/shops/${shopId}/held-carts/${id}`,
  HELD_CART_BY_CODE: (shopId: number, code: string) => `/api/shops/${shopId}/held-carts/code/${code}`,
  RETRIEVE_HELD_CART: (shopId: number, id: number) => `/api/shops/${shopId}/held-carts/${id}/retrieve`,
  HOLD_CART: (shopId: number) => `/api/shops/${shopId}/held-carts`,
  CANCEL_HELD_CART: (shopId: number, cartId: number) =>
      `/api/shops/${shopId}/held-carts/${cartId}`,

  // Checkout
  CHECKOUT: (shopId: number) => `/api/shops/${shopId}/checkout`,
  REFUND: (shopId: number) => `/api/shops/${shopId}/refund`,
  VOID_TRANSACTION: (shopId: number, receiptNumber: string) => 
    `/api/shops/${shopId}/transactions/${receiptNumber}/void`,

  // Transactions
  TRANSACTIONS: (shopId: number) => `/api/shops/${shopId}/transactions`,
  TRANSACTIONS_BY_DATE: (shopId: number, date: string) => `/api/shops/${shopId}/transactions/date/${date}`,
  TRANSACTION_DETAILS: (shopId: number, receiptNumber: string) => 
    `/api/shops/${shopId}/transactions/${receiptNumber}`,
  
  // Receipts
  RECEIPT: (shopId: number, receiptNumber: string) => `/api/shops/${shopId}/receipts/${receiptNumber}`,
  RECEIPT_HTML: (shopId: number, receiptNumber: string) => `/api/shops/${shopId}/receipts/${receiptNumber}/html`,
  RECEIPT_TEXT: (shopId: number, receiptNumber: string) => `/api/shops/${shopId}/receipts/${receiptNumber}/text`,
  RECEIPT_ESCPOS: (shopId: number, receiptNumber: string) => `/api/shops/${shopId}/receipts/${receiptNumber}/escpos`,

  // Payment Accounts
  PAYMENT_ACCOUNTS: (shopId: number) => `/api/shops/${shopId}/payment-accounts`,

  // Payment Methods
  PAYMENT_METHODS_LIST: (userId: number, shopId: number) =>
    `/api/users/${userId}/shops/${shopId}/payment-method`,

  // Dashboard
  DASHBOARD: (shopId: number) => `/api/shops/${shopId}/dashboard`,

  // Reports
  DAILY_REPORT: (shopId: number) => `/api/shops/${shopId}/reports/daily`,

} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  USER: 'pos_user',
  SELECTED_SHOP: 'pos_selected_shop',
  CART_BACKUP: 'pos_cart_backup',
  AUTH_TIMESTAMP: 'pos_auth_timestamp',
  SHOP_CONFIG: 'pos_shop_config',
  BROWSER_ID: 'pos_browser_id',
  POS_SESSION: 'pos_session',
  FCM_ROW_ID: 'pos_fcm_row_id',
  CHECKOUT_QUEUE: 'pos_checkout_queue',
  /** Survives clearShopSelection so Shop select can mark "Terakhir dibuka" */
  LAST_SHOP_ID: 'pos_last_shop_id',
} as const;

// Order types ride inside checkout `notes` as a "[TAG] " prefix (backend has no field for it)
export const ORDER_TYPES = ['DINE_IN', 'TAKEAWAY', 'DRIVE_THRU'] as const;

export const SUPPORT_WHATSAPP = { number: '6282345671097', display: '+62 823-4567-1097' } as const;

export const MIN_TOPUP_AMOUNT = 10_000;
export const POS_SESSION_REFRESH_MS = 11 * 60 * 60 * 1000; // server expiry 12h, refresh 1h early

// UI Constants
export const DEBOUNCE_DELAY = 300; // ms
export const TOAST_DURATION = 3000; // ms
export const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes in ms
export const MAX_CART_ITEMS = 100;
export const MIN_SEARCH_LENGTH = 2;

// Validation
export const PHONE_REGEX = /^[0-9]{10,15}$/;
export const BARCODE_REGEX = /^\d{8,13}$/;
export const AUTH_CODE_REGEX = /^[0-9]{6}$/;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'dd MMM yyyy',
  DISPLAY_WITH_TIME: 'dd MMM yyyy HH:mm',
  API: 'yyyy-MM-dd',
  TIME_ONLY: 'HH:mm',
  FULL: 'dd MMMM yyyy, HH:mm:ss',
} as const;

// Currency
export const CURRENCY = {
  SYMBOL: 'Rp',
  CODE: 'IDR',
  LOCALE: 'id-ID',
} as const;

// Grid/Layout
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
} as const;

// App Metadata
export const APP_NAME = 'RUNUT POS';
export const APP_VERSION: string = pkg.version;
export const APP_DESCRIPTION = 'Point of Sale System';

// Feature Flags
export const FEATURES = {
  BARCODE_SCANNER: true,
  OFFLINE_MODE: false,
  RECEIPT_PRINT: true,
  REFUNDS: true,
  HELD_CARTS: true,
} as const;